import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ModulosProvider } from './context/ModulosContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import AppErrorBoundary from './components/AppErrorBoundary';
import PwaExperience from './components/PwaExperience';
import { FinancialWorkspaceProvider } from './context/FinancialWorkspaceContext';
import RoleLandingRedirect from './components/RoleLandingRedirect';

// Pages - Core
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ContinuityDashboardPage = lazy(() => import('./pages/ContinuityDashboardPage'));
const ControlCenterPage = lazy(() => import('./pages/ControlCenterPage'));
const CondominiosPage = lazy(() => import('./pages/CondominiosPage'));
const UsuariosPage = lazy(() => import('./pages/UsuariosPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AsistenciaPage = lazy(() => import('./pages/AsistenciaPage'));
const AsistenciaRegistrarPage = lazy(() => import('./pages/AsistenciaRegistrarPage'));
const IncidenciasPage = lazy(() => import('./pages/IncidenciasPage'));
const AlertasPage = lazy(() => import('./pages/AlertasPage'));
const AuditoriaPage = lazy(() => import('./pages/AuditoriaPage'));
const ConfigurationCenterPage = lazy(() => import('./pages/ConfigurationCenterPage'));

// Pages - Mis Finanzas
const AdministrationPage = lazy(() => import('./pages/AdministrationPage'));
const FinanzasPage = lazy(() => import('./pages/FinanzasPage'));

// Pages - Checklists (Fase 3)
const ChecklistsPage = lazy(() => import('./pages/ChecklistsPage'));
const ChecklistEjecucionPage = lazy(() => import('./pages/ChecklistEjecucionPage'));
const ChecklistPlantillasPage = lazy(() => import('./pages/ChecklistPlantillasPage'));
const ChecklistKPIsPage = lazy(() => import('./pages/ChecklistKPIsPage'));

// Pages - Incidencias
const IncidenciaDetallePage = lazy(() => import('./pages/IncidenciaDetallePage'));

// Pages - Documentos (Biblioteca Institucional)
const DocumentosPage = lazy(() => import('./pages/DocumentosPage'));

// Pages - NEW MODULES (Acuerdos, Minutas, Reportes) - March 2026
const AcuerdosPage = lazy(() => import('./pages/AcuerdosPage'));
const MinutasPage = lazy(() => import('./pages/MinutasPage'));
const OperacionKPIsPage = lazy(() => import('./pages/OperacionKPIsPage'));
const CalendarioActividadesPage = lazy(() => import('./pages/CalendarioActividadesPage'));
const PettyCashPage = lazy(() => import('./pages/PettyCashPage'));
const ProvidersPage = lazy(() => import('./pages/ProvidersPage'));
const QuotationsPage = lazy(() => import('./pages/QuotationsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const AcademyPage = lazy(() => import('./pages/AcademyPage'));
const AcademyPracticePage = lazy(() => import('./pages/AcademyPracticePage'));
const AcademyEvaluationPage = lazy(() => import('./pages/AcademyEvaluationPage'));
const SurveysPage = lazy(() => import('./pages/SurveysPage'));
const SurveyPublicPage = lazy(() => import('./pages/SurveyPublicPage'));
const RecruitmentPublicPage = lazy(() => import('./pages/RecruitmentPublicPage'));
const RecruitmentManagementPage = lazy(() => import('./pages/RecruitmentManagementPage'));
const MaintenancesPage = lazy(() => import('./pages/MaintenancesPage'));
const CommonAreasPage = lazy(() => import('./pages/CommonAreasPage'));
const CorporateManagementPage = lazy(() => import('./pages/CorporateManagementPage'));
const DirectionCenterPage = lazy(() => import('./pages/DirectionCenterPage'));
const FinancialTransparencyPage = lazy(() => import('./pages/FinancialTransparencyPage'));
const CouncilDashboardPage = lazy(() => import('./pages/CouncilDashboardPage'));
const CouncilActionsAdminPage = lazy(() => import('./pages/CouncilActionsAdminPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const NeighborAttentionPage = lazy(() => import('./pages/NeighborAttentionPage'));
const PublicNeighborReportPage = lazy(() => import('./pages/PublicNeighborReportPage'));
const AccessControlPage = lazy(() => import('./pages/AccessControlPage'));
const PublicAccessPortalPage = lazy(() => import('./pages/PublicAccessPortalPage'));
const WelcomePublicPage = lazy(() => import('./pages/WelcomePublicPage'));
const PublicExperienceTourPage = lazy(() => import('./pages/PublicExperienceTourPage'));
const PrivacyNoticePage = lazy(() => import('./pages/PrivacyNoticePage'));
const SanctionsPage = lazy(() => import('./pages/SanctionsPage'));

import './App.css';
import './module-experience.css';

function FinanceLegacyRedirect({ to, tab }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (tab) params.set('tab', tab);
  const target = `${to}?${params.toString()}${tab ? `#${tab}` : ''}`;
  useEffect(() => { window.location.replace(target); }, [target]);
  return <div className="flex h-40 items-center justify-center text-sm text-slate-500">Abriendo el espacio financiero…</div>;
}

function RetiredFinanceRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const path = location.pathname.toLowerCase();
  let section = 'inicio';
  if (path.includes('cobranza') || path.includes('cargos') || path.includes('estado-cuenta')) section = 'cobranza';
  else if (path.includes('ingresos')) section = 'ingresos';
  else if (path.includes('egresos')) section = 'egresos';
  else if (path.includes('movimientos')) section = params.get('tab') === 'egresos' ? 'egresos' : 'ingresos';
  else if (path.includes('banco') || path.includes('conciliar')) section = 'banco';
  else if (path.includes('reportes') || path.includes('inteligencia')) section = 'reportes';
  else if (path.includes('configuracion') || path.includes('operacion-ia') || path.includes('automatizacion')) section = 'configuracion';
  params.delete('tab');
  params.set('seccion', section);
  return <Navigate to={`/condo-ar/finanzas?${params.toString()}`} replace />;
}

function RouteBranding() {
  const location = useLocation();

  useEffect(() => {
    const isAres = location.pathname.startsWith('/ares-seguridad');
    const values = isAres ? {
      title: 'Ares Seguridad',
      manifest: '/manifest-ares.webmanifest',
      icon: '/icons/ares-icon-v2-192.png',
      iconType: 'image/png',
      theme: '#090909',
      description: 'Operación de caseta, rondines, paquetería y seguridad.',
    } : {
      title: 'AR Gestión Integral',
      manifest: '/manifest.webmanifest',
      icon: '/icons/ar-logo-tab-v2.svg',
      iconType: 'image/svg+xml',
      theme: '#8A1C2C',
      description: 'Sistema de gestión integral para condominios',
    };

    document.title = values.title;
    const manifest = document.querySelector('link[rel="manifest"]');
    if (manifest) manifest.setAttribute('href', values.manifest);
    const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleIcon) appleIcon.setAttribute('href', values.icon);
    const favicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    favicons.forEach((favicon) => {
      favicon.setAttribute('href', values.icon);
      favicon.setAttribute('type', values.iconType);
    });
    const theme = document.querySelector('meta[name="theme-color"]');
    if (theme) theme.setAttribute('content', values.theme);
    const appTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appTitle) appTitle.setAttribute('content', isAres ? 'Ares' : 'AR Gestión');
    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute('content', values.description);
  }, [location.pathname]);

  return null;
}

function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <AppErrorBoundary resetKey={`${location.pathname}${location.search}`}>{children}</AppErrorBoundary>;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ModulosProvider>
          <BrowserRouter>
            <RouteBranding />
            <FinancialWorkspaceProvider>
            <RouteErrorBoundary>
            <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-center"><div><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#8A1C2C]" /><p className="mt-4 text-sm font-medium text-slate-600">Preparando tu espacio…</p></div></div>}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reporte-vecinal/:token" element={<PublicNeighborReportPage />} />
              <Route path="/acceso/:slug/:token" element={<PublicAccessPortalPage />} />
              <Route path="/bienvenida/:token" element={<WelcomePublicPage />} />
              <Route path="/conoce-ar" element={<PublicExperienceTourPage />} />
              <Route path="/conoce-ar/:profile" element={<PublicExperienceTourPage />} />
              <Route path="/aviso-de-privacidad" element={<PrivacyNoticePage />} />
              <Route path="/encuesta/:token" element={<SurveyPublicPage />} />
              <Route path="/postulacion/:token" element={<RecruitmentPublicPage />} />
              <Route path="/postulacion/abierta/:token" element={<RecruitmentPublicPage openRegistration />} />
              <Route path="/app" element={<ProtectedRoute><RoleLandingRedirect /></ProtectedRoute>} />
              
              {/* Protected routes - Core */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/continuidad" element={<ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}><Layout><ContinuityDashboardPage /></Layout></ProtectedRoute>} />
            <Route path="/control-integral" element={<ProtectedRoute requiredRoles={['direccion']}><Layout><ControlCenterPage /></Layout></ProtectedRoute>} />
            <Route path="/consejo/*" element={<ProtectedRoute requiredRoles={['consejo', 'direccion']}><Layout><CouncilDashboardPage /></Layout></ProtectedRoute>} />
            <Route path="/vista-consejo/*" element={<ProtectedRoute requiredRoles={['direccion']}><CouncilDashboardPage /></ProtectedRoute>} />
            <Route path="/gestiones-consejo" element={<ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}><Layout><CouncilActionsAdminPage /></Layout></ProtectedRoute>} />
            <Route path="/transparencia-financiera" element={<ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}><Layout><FinancialTransparencyPage /></Layout></ProtectedRoute>} />
            <Route path="/reclutamiento" element={<ProtectedRoute><Layout><RecruitmentManagementPage /></Layout></ProtectedRoute>} />
            
            <Route
              path="/direccion/*"
              element={
                <ProtectedRoute requiredRoles={['direccion']}>
                  <Layout>
                    <DirectionCenterPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/gestion-corporativa"
              element={
                <ProtectedRoute requiredRoles={['direccion']} requiredModulo="gestion_corporativa">
                  <Layout>
                    <CorporateManagementPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/condominios"
              element={
                <ProtectedRoute requiredRoles={['direccion']}>
                  <Layout>
                    <CondominiosPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/usuarios"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Layout>
                    <UsuariosPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/usuarios/:userId"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Layout>
                    <UsuariosPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/asistencia"
              element={
                <ProtectedRoute requiredModulo="asistencia">
                  <Layout>
                    <AsistenciaPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/asistencia/registrar"
              element={
                <ProtectedRoute requiredRoles={['administrador']}>
                  <Layout>
                    <AsistenciaRegistrarPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Checklists Routes */}
            <Route
              path="/checklists"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'guardia', 'jardineria', 'mantenimiento']}>
                  <Layout>
                    <ChecklistsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/checklists/ejecucion/:id"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'guardia', 'jardineria', 'mantenimiento']}>
                  <Layout>
                    <ChecklistEjecucionPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/checklists/plantillas"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor']}>
                  <Layout>
                    <ChecklistPlantillasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/checklists/kpis"
              element={
                <ProtectedRoute requiredRoles={['direccion']}>
                  <Layout>
                    <ChecklistKPIsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/incidencias"
              element={
                <ProtectedRoute>
                  <Layout>
                    <IncidenciasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/incidencias/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <IncidenciaDetallePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/documentos"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DocumentosPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/documentos/encuestas"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']} requiredModulo="documentos">
                  <Layout><SurveysPage /></Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/alertas"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AlertasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/proveedores"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="proveedores">
                  <Layout>
                    <ProvidersPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/proveedores/:id"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="proveedores">
                  <Layout><ProvidersPage /></Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/cotizaciones"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="cotizaciones">
                  <Layout>
                    <QuotationsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cotizaciones/:id"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="cotizaciones">
                  <Layout><QuotationsPage /></Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/soporte"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']} requiredModulo="soporte_ar">
                  <Layout>
                    <SupportPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/mantenimientos"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="mantenimientos">
                  <Layout>
                    <MaintenancesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/mantenimientos/:id"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="mantenimientos">
                  <Layout>
                    <MaintenancesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/areas-comunes"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="areas_comunes">
                  <Layout>
                    <CommonAreasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventario"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="inventario">
                  <Layout>
                    <InventoryPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventario/:id"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="inventario">
                  <Layout>
                    <InventoryPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/atencion-vecinal"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']} requiredModulo="atencion_vecinal">
                  <Layout>
                    <NeighborAttentionPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/administracion/residentes"
              element={<Navigate to="/administracion/unidades" replace />}
            />
            <Route path="/gestion-residentes" element={<Navigate to="/administracion/unidades" replace />} />

            <Route
              path="/access-control"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'guardia']} requiredModulo="access_control">
                  <Layout>
                    <AccessControlPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/access-control/:section"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'guardia']} requiredModulo="access_control">
                  <Layout>
                    <AccessControlPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ares-seguridad"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'guardia']} requiredModulo="access_control">
                  <Layout><AccessControlPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ares-seguridad/:section"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'guardia']} requiredModulo="access_control">
                  <Layout><AccessControlPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/auditoria"
              element={
                <ProtectedRoute requiredRoles={['direccion']}>
                  <Layout>
                    <AuditoriaPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Mis Finanzas es el único espacio financiero visible. */}
            <Route
              path="/administracion/unidades"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Layout><AdministrationPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/administracion/unidades/:unitId"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Layout><AdministrationPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/financiero/unidades"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Navigate to="/administracion/unidades" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finanzas"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']}>
                  <FinanceLegacyRedirect to="/condo-ar/finanzas" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/condo-ar"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']}>
                  <FinanceLegacyRedirect to="/condo-ar/finanzas" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/condo-ar/finanzas"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']}>
                  <Layout><FinanzasPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/financiero/*"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']}>
                  <RetiredFinanceRedirect />
                </ProtectedRoute>
              }
            />

            {/* Other routes */}
            <Route
              path="/perfil"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProfilePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/configuracion"
              element={
                <ProtectedRoute requiredRoles={['direccion']}>
                  <Layout><ConfigurationCenterPage /></Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Redirects */}
            <Route path="/" element={<ProtectedRoute><RoleLandingRedirect /></ProtectedRoute>} />
            <Route path="*" element={<ProtectedRoute><RoleLandingRedirect /></ProtectedRoute>} />
            
            {/* NEW MODULES - Acuerdos, Minutas, Reportes (March 2026) */}
            <Route 
              path="/acuerdos" 
              element={
                <ProtectedRoute requiredModulo="acuerdos">
                  <Layout>
                    <AcuerdosPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/acuerdos/:agreementId" element={<ProtectedRoute requiredModulo="acuerdos"><Layout><AcuerdosPage /></Layout></ProtectedRoute>} />
            <Route 
              path="/minutas" 
              element={
                <ProtectedRoute requiredModulo="minutas">
                  <Layout>
                    <MinutasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/caja-chica"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="caja_chica">
                  <Layout>
                    <PettyCashPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/caja-chica/:boxId"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="caja_chica">
                  <Layout>
                    <PettyCashPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/caja-chica/:boxId/movimiento/:movementId"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="caja_chica">
                  <Layout>
                    <PettyCashPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/juntas/calendario-actividades"
              element={
                <ProtectedRoute requiredModulo="calendario_operativo">
                  <Layout>
                    <CalendarioActividadesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendario-operativo"
              element={
                <ProtectedRoute requiredModulo="calendario_operativo">
                  <Layout>
                    <CalendarioActividadesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sanciones"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="sanciones">
                  <Layout>
                    <SanctionsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {['redactar', 'consultar', 'reportes', 'biblioteca'].map((section) => (
              <Route
                key={`sanciones-${section}`}
                path={`/sanciones/${section}`}
                element={
                  <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']} requiredModulo="sanciones">
                    <Layout><SanctionsPage /></Layout>
                  </ProtectedRoute>
                }
              />
            ))}
            <Route
              path="/sanciones/:id"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="sanciones">
                  <Layout><SanctionsPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sanciones/:id/:section"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador', 'consejo']} requiredModulo="sanciones">
                  <Layout><SanctionsPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/reportes" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <FinanzasPage reportsOnly />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reportes/transparencia"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Layout>
                    <FinanzasPage reportsOnly initialReportsView="transparencia" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/academia"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Layout><AcademyPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/academia/practica"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Layout><AcademyPracticePage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/academia/evaluacion"
              element={
                <ProtectedRoute requiredRoles={['direccion', 'supervisor', 'administrador']}>
                  <Layout><AcademyEvaluationPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/reportes/mensual" element={<Navigate to="/reportes" replace />} />
            <Route
              path="/reportes/operacion-kpis"
              element={
                <ProtectedRoute requiredRoles={['direccion']}>
                  <Layout><OperacionKPIsPage /></Layout>
                </ProtectedRoute>
              }
            />
            </Routes>
            </Suspense>
            </RouteErrorBoundary>
            </FinancialWorkspaceProvider>
        </BrowserRouter>
        <Toaster position="top-right" />
        <PwaExperience />
        </ModulosProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
